import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Home } from "lucide-react";
import { API } from "@/lib/api";

export default function AuthPage({ mode = "login" }) {
  const { login, signup } = useAuth();
  const nav = useNavigate();
  const isSignup = mode === "signup";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("seeker");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isSignup) {
        await signup({ email, password, name, role });
        toast.success("Welcome to Flock!");
        nav("/browse?onboarding=1");
      } else {
        await login(email, password);
        toast.success("Welcome back");
        nav("/browse");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    window.location.href = `${API}/auth/google?redirect_to=${encodeURIComponent(window.location.origin)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-[#2A4B3C]">
        <img
          src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200"
          alt=""
          className="w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#2A4B3C]/70 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="text-xs uppercase tracking-[0.2em] opacity-80 mb-3">Flock · Find your home</div>
          <div className="font-display text-4xl font-medium leading-tight">
            A home is more than a listing. Ours are broker-free.
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-xl brand-primary grid place-items-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-semibold text-lg">Flock</span>
          </Link>
          <h1 className="font-display text-3xl font-medium">
            {isSignup ? "Create your account" : "Welcome back"}
          </h1>
          <p className="text-stone-600 mt-2 mb-6">
            {isSignup ? "Start finding a home, or list one." : "Log in to continue your search."}
          </p>

          <Button
            data-testid="google-login-btn"
            variant="outline"
            className="w-full rounded-full border-stone-300 h-11"
            onClick={googleLogin}
          >
            <img alt="" src="https://www.google.com/favicon.ico" className="w-4 h-4 mr-2" />
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-stone-400">
            <div className="h-px bg-stone-200 flex-1" /> or with email <div className="h-px bg-stone-200 flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {isSignup && (
              <>
                <div>
                  <Label>Full name</Label>
                  <Input data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Priya Sharma" />
                </div>
                <div>
                  <Label>I'm looking to</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {[["seeker", "Find a place"], ["lister", "List a place"]].map(([v, l]) => (
                      <button
                        type="button" key={v}
                        data-testid={`role-${v}`}
                        onClick={() => setRole(v)}
                        className={`p-3 rounded-xl border text-sm font-medium transition ${role === v ? "border-[#2A4B3C] bg-[#2A4B3C]/5 text-[#2A4B3C]" : "border-stone-200 text-stone-600 hover:border-stone-300"
                          }`}
                      >{l}</button>
                    ))}
                  </div>
                </div>
              </>
            )}
            <div>
              <Label>Email</Label>
              <Input data-testid="auth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com" />
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="auth-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="At least 6 characters" />
            </div>
            <Button data-testid="auth-submit" type="submit" disabled={loading} className="w-full brand-accent rounded-full h-11">
              {loading ? "Please wait…" : isSignup ? "Create account" : "Log in"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-stone-600">
            {isSignup ? (
              <>Already have an account?{" "}
                <Link data-testid="switch-to-login" to="/login" className="text-brand font-medium">Log in</Link>
              </>
            ) : (
              <>New to Flock?{" "}
                <Link data-testid="switch-to-signup" to="/signup" className="text-brand font-medium">Sign up</Link>
              </>
            )}
          </div>
          <div className="mt-3 text-center text-xs text-stone-400">
            Try: owner@roomly.test / seeker@roomly.test · pw: password123
          </div>
        </div>
      </div>
    </div>
  );
}
